import { CS2_PROVIDER_SCHEMA } from './provider-protocol.js';

const DEMO_GAIT_CYCLE_UNITS = 128;

const positionOf = (value) => {
  const p = value?.m_position || value?.position || value || {};
  return {
    x: Number(p.x || 0),
    y: Number(p.y || 0),
    z: Number(p.z || 0),
  };
};

const finitePosition = (position) => Object.values(position).every(Number.isFinite);

function normalizeProjectileType(value) {
  const type = String(value || 'grenade').toLowerCase();
  if (type.includes('smoke')) return 'smoke';
  if (type.includes('flash')) return 'flashbang';
  if (type.includes('hegrenade') || type === 'he' || type.includes('frag')) return 'hegrenade';
  if (type.includes('molotov') || type.includes('incendiary') || type.includes('inferno')) return 'inferno';
  return type;
}

export function normalizeCs2MapId(value) {
  const raw = String(value ?? '').trim().toLowerCase().replaceAll('\\', '/');
  const match = raw.match(/(?:^|\/)(de_[a-z0-9_]+)(?:\.[a-z0-9]+)?(?:$|\/)/)
    || raw.match(/(de_[a-z0-9_]+)/);
  return match ? match[1] : raw.replace(/^dynamic_/, '').replace(/^maps\//, '');
}

function unwrap(raw) {
  if (!raw || typeof raw !== 'object') throw new Error('CS2RADAR-FRAME-001: JSON object required');
  if (raw.schemaVersion === CS2_PROVIDER_SCHEMA && raw.data && typeof raw.data === 'object') return raw.data;
  if (raw.type === 'game_data' && raw.data && typeof raw.data === 'object') return raw.data;
  if (raw.payload && typeof raw.payload === 'object') return raw.payload;
  return raw;
}

export function adaptCs2Frame(input) {
  const raw = unwrap(input);
  const mapValue = raw.m_map ?? raw.map ?? 'dynamic_unknown';
  const mapId = typeof mapValue === 'string' ? mapValue : (mapValue.id || mapValue.name || mapValue.levelName);

  const players = (raw.m_players || raw.players || []).map((player, index) => {
    const position = positionOf(player);
    const velocity = positionOf(player.m_velocity ?? player.velocity);
    const movementState = String(player.m_movement_state ?? player.movementState ?? '').toLowerCase();
    const speedUnitsPerSecond = Math.hypot(velocity.x, velocity.y);
    const movementAction = movementState === 'running' ? 'run'
      : movementState === 'walking' ? 'walk'
        : movementState === 'dead' ? 'death' : 'hold';
    const actionState = String(player.m_action_state ?? player.actionState ?? '').toLowerCase();
    const liveAction = actionState === 'dead' ? 'death'
      : ['fire', 'reload', 'scoped', 'defuse', 'flashed'].includes(actionState)
        ? actionState
        : movementAction;
    const actionSequence = Math.max(0,
      Number(player.m_action_sequence ?? player.actionSequence ?? 0) || 0);
    const actionProgress = liveAction === 'fire' ? 0.34
      : liveAction === 'reload' ? 0.55
        : liveAction === 'defuse' ? 0.72
          : liveAction === 'flashed' ? 0.58 : 0;
    const timestampMs = Number(input?.timestamp ?? raw.timestamp ?? Date.now());
    const derivedAnimation = movementState || actionState ? {
      action: liveAction,
      actionState: actionState || 'hold',
      actionSequence,
      actionProgress,
      locomotionCycle: ((timestampMs / 1000) * speedUnitsPerSecond / DEMO_GAIT_CYCLE_UNITS) % 1,
      speedUnitsPerSecond,
      stance: player.m_stance ?? player.stance ?? 'unknown',
    } : null;
    return {
      id: player.m_idx ?? player.id ?? index,
      name: player.m_name ?? player.name ?? `Player ${index + 1}`,
      team: Number(player.m_team ?? player.team ?? 0),
      health: Number(player.m_health ?? player.hp ?? 0),
      armor: Number(player.m_armor ?? player.armor ?? 0),
      dead: Boolean(player.m_is_dead ?? player.dead),
      local: Boolean(player.m_is_local ?? player.local),
      angle: Number(player.m_eye_angle ?? player.angle ?? 0),
      weapon: player.m_weapons?.m_active?.m_name
        ?? (typeof player.weapon === 'string' ? player.weapon : player.weapon?.name)
        ?? '',
      position,
      velocity,
      animation: player.m_animation && typeof player.m_animation === 'object'
        ? { ...player.m_animation }
        : derivedAnimation,
      modelName: player.m_model_name ?? player.modelName ?? '',
      modelFamily: player.m_model_family ?? player.modelFamily ?? '',
      stance: player.m_stance ?? player.stance ?? 'unknown',
      movementState,
      actionState: actionState || '',
      actionSequence,
      snapshotAgeMs: Math.max(0, Number(player.m_snapshot_age_ms ?? player.snapshotAgeMs ?? 0) || 0),
      dataConfidence: player.m_data_confidence ?? player.dataConfidence ?? '',
      hasBomb: Boolean(player.m_has_bomb ?? player.hasBomb),
    };
  }).filter((player) => finitePosition(player.position));

  const bombRaw = raw.m_bomb ?? raw.bomb;
  const bomb = bombRaw ? {
    position: positionOf(bombRaw),
    planted: 'm_blow_time' in bombRaw || Boolean(bombRaw.m_is_planted) || bombRaw.state === 'planted',
    defused: Boolean(bombRaw.m_is_defused) || bombRaw.state === 'defused',
  } : null;

  const projectiles = (raw.m_projectiles || raw.projectiles || []).map((item, index) => {
    const exploded = Boolean(item.m_exploded ?? item.exploded ?? item.state === 'detonated');
    const state = String(item.m_state ?? item.state ?? (exploded ? 'detonated' : 'flight')).toLowerCase();
    return {
      id: item.m_entity_id ?? item.id ?? index,
      type: normalizeProjectileType(item.m_type ?? item.type),
      team: Number(item.m_team ?? item.team ?? 0),
      state,
      exploded,
      effectAgeMs: Math.max(0, Number(item.m_effect_age_ms ?? item.effectAgeMs ?? 0) || 0),
      effectDurationMs: Math.max(0, Number(item.m_effect_duration_ms ?? item.effectDurationMs ?? 0) || 0),
      effectRadius: Math.max(0, Number(item.m_effect_radius ?? item.effectRadius ?? 0) || 0),
      position: positionOf(item),
      velocity: positionOf(item.m_velocity ?? item.velocity),
    };
  }).filter((item) => finitePosition(item.position));

  const droppedWeapons = (raw.m_dropped_weapons || raw.droppedWeapons || []).map((item, index) => ({
    id: item.m_entity_id ?? item.id ?? index,
    name: item.m_name ?? item.name ?? 'weapon',
    position: positionOf(item),
  })).filter((item) => finitePosition(item.position));

  return {
    schema: 'csgo2-radar/frame/v1',
    mapId: normalizeCs2MapId(mapId),
    sequence: Number(input?.sequence ?? raw.sequence ?? raw.m_sequence ?? Date.now()),
    timestamp: Number(input?.timestamp ?? raw.timestamp ?? Date.now()),
    localTeam: Number(raw.m_local_team ?? raw.localTeam ?? raw.local?.team ?? 0),
    roundPhase: raw.m_round_phase ?? raw.roundPhase ?? raw.round?.phase ?? '',
    players,
    bomb,
    projectiles,
    droppedWeapons,
    capabilities: input?.capabilities || raw.capabilities || null,
  };
}

const seeds = [
  [1088.24, 111.94, -100.89],
  [-554.96, -2103.72, -116],
  [-1419.46, -2189.19, -176.84],
  [-1862.97, 177.04, -96.8],
  [1380.98, -1479.9, -104],
  [-432, -812, -104],
];

export function createDemoFrame(time = performance.now()) {
  const t = time / 1000;
  const players = seeds.map(([x, y, z], index) => ({
    m_idx: index + 1,
    m_name: index === 0 ? '本机' : `${index < 3 ? 'CT' : 'T'}-${index + 1}`,
    m_team: index < 3 ? 3 : 2,
    m_health: index === 4 ? 47 : 100,
    m_armor: 100,
    m_is_dead: false,
    m_is_local: index === 0,
    m_eye_angle: (time / 35 + index * 52) % 360,
    m_position: {
      x: x + Math.sin(t * 0.45 + index) * (index ? 75 : 12),
      y: y + Math.cos(t * 0.4 + index) * (index ? 75 : 12),
      z,
    },
    m_weapons: { m_active: { m_name: index % 2 ? 'ak47' : 'm4a1' } },
  }));
  return adaptCs2Frame({
    m_map: 'de_mirage',
    m_local_team: 3,
    m_round_phase: 'live',
    m_players: players,
    m_bomb: { x: -520, y: -1810, z: -116, m_is_planting: false },
    m_projectiles: [
      { m_entity_id: 1, m_type: 'smoke', m_team: 2, m_position: { x: 180, y: -890, z: -80 } },
      { m_entity_id: 2, m_type: 'inferno', m_team: 3, m_position: { x: -910, y: -1400, z: -116 } },
    ],
    m_dropped_weapons: [
      { m_entity_id: 3, m_name: 'awp', m_position: { x: -730, y: -1680, z: -112 } },
    ],
  });
}

const DEMO_LABELS = Object.freeze({
  ct_spawn: 'CT 出生点',
  ct_spawn_center: 'CT 出生点',
  t_spawn: 'T 出生点',
  t_spawn_center: 'T 出生点',
  a_site_center: 'A 区',
  b_site_center: 'B 区',
  b_site_center_revalidated: 'B 区',
  mid_center: '中路',
});

const readableDemoLabel = (label, index) => DEMO_LABELS[label]
  || String(label || `实测点 ${index + 1}`).replaceAll('_', ' ');

export function createResourceDemoFrame(mapId, liveValidation, time = performance.now()) {
  const normalizedMapId = normalizeCs2MapId(mapId);
  const samples = (Array.isArray(liveValidation?.samples) ? liveValidation.samples : [])
    .filter((sample) => [sample?.x, sample?.y, sample?.z].every((value) => Number.isFinite(Number(value))));
  if (samples.length < 3) {
    if (normalizedMapId === 'de_mirage') return createDemoFrame(time);
    return adaptCs2Frame({ m_map: normalizedMapId, m_round_phase: 'preview', m_players: [] });
  }

  const localIndex = Math.max(0, samples.findIndex((sample) => String(sample.label).includes('ct_spawn')));
  const t = time / 1000;
  const players = samples.slice(0, 8).map((sample, index) => {
    const label = String(sample.label || '');
    const local = index === localIndex;
    const team = label.includes('ct_spawn') ? 3 : (label.includes('t_spawn') ? 2 : (local ? 3 : (index % 2 ? 2 : 3)));
    const radius = label.includes('spawn') ? 8 : 18;
    return {
      m_idx: index + 1,
      m_name: local ? '本机 · 实机锚点' : readableDemoLabel(label, index),
      m_team: team,
      m_health: index === samples.length - 1 ? 64 : 100,
      m_armor: 100,
      m_is_dead: false,
      m_is_local: local,
      m_eye_angle: (time / 42 + index * 47) % 360,
      m_position: {
        x: Number(sample.x) + Math.sin(t * 0.34 + index) * radius,
        y: Number(sample.y) + Math.cos(t * 0.31 + index) * radius,
        z: Number(sample.z),
      },
      m_weapons: { m_active: { m_name: team === 3 ? 'm4a1' : 'ak47' } },
    };
  });
  const bombSample = samples.find((sample) => String(sample.label).includes('a_site')) || samples[1];
  const utilitySample = samples.find((sample) => /mid|ramp|connector/.test(String(sample.label))) || samples[2];
  const droppedSample = samples.at(-1);
  return adaptCs2Frame({
    m_map: normalizedMapId,
    m_local_team: 3,
    m_round_phase: 'preview',
    m_players: players,
    m_bomb: { x: bombSample.x, y: bombSample.y, z: bombSample.z, m_is_planting: false },
    m_projectiles: [{
      m_entity_id: 7001,
      m_type: 'smoke',
      m_team: 2,
      m_position: { x: utilitySample.x, y: utilitySample.y, z: utilitySample.z },
    }],
    m_dropped_weapons: [{
      m_entity_id: 8001,
      m_name: 'awp',
      m_position: { x: droppedSample.x, y: droppedSample.y, z: droppedSample.z },
    }],
  });
}

function routeMetrics(path) {
  const points = (Array.isArray(path) ? path : []).filter((point) => (
    [point?.x, point?.y, point?.z].every((value) => Number.isFinite(Number(value)))
  ));
  const lengths = [];
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const distance = Math.hypot(
      Number(points[index].x) - Number(points[index - 1].x),
      Number(points[index].y) - Number(points[index - 1].y),
      Number(points[index].z) - Number(points[index - 1].z),
    );
    lengths.push(distance);
    total += distance;
  }
  return { points, lengths, total };
}

function interpolateRouteDistance(path, requestedDistance) {
  const metrics = routeMetrics(path);
  const { points, lengths, total } = metrics;
  if (points.length < 2) return {
    position: positionOf(points[0]), angle: 0, progress: 0, totalDistance: total,
  };
  let remaining = Math.max(0, Math.min(total, Number(requestedDistance) || 0));
  for (let index = 0; index < lengths.length; index += 1) {
    if (remaining > lengths[index] && index < lengths.length - 1) {
      remaining -= lengths[index];
      continue;
    }
    const start = points[index];
    const end = points[index + 1];
    const t = lengths[index] > 0 ? Math.min(1, remaining / lengths[index]) : 1;
    return {
      position: {
        x: Number(start.x) + (Number(end.x) - Number(start.x)) * t,
        y: Number(start.y) + (Number(end.y) - Number(start.y)) * t,
        z: Number(start.z) + (Number(end.z) - Number(start.z)) * t,
      },
      angle: Math.atan2(Number(end.y) - Number(start.y), Number(end.x) - Number(start.x)) * 180 / Math.PI + 90,
      progress: total > 0 ? Math.max(0, Math.min(1, (Number(requestedDistance) || 0) / total)) : 1,
      totalDistance: total,
    };
  }
  return { position: positionOf(points.at(-1)), angle: 0, progress: 1, totalDistance: total };
}

function trapezoidDistanceAtTime(totalDistance, travelDurationMs, accelerationMs, timeMs) {
  const distance = Math.max(0, Number(totalDistance) || 0);
  const duration = Math.max(1, Number(travelDurationMs) || 1);
  const acceleration = Math.min(duration / 2, Math.max(0, Number(accelerationMs) || 0));
  const time = Math.max(0, Math.min(duration, Number(timeMs) || 0));
  if (distance <= 0 || time <= 0) return 0;
  if (time >= duration) return distance;
  if (acceleration < 1) return distance * time / duration;
  const peakSpeedPerMs = distance / Math.max(1, duration - acceleration);
  if (time < acceleration) return 0.5 * peakSpeedPerMs * time * time / acceleration;
  if (time <= duration - acceleration) {
    return peakSpeedPerMs * (time - acceleration / 2);
  }
  const remaining = duration - time;
  return distance - 0.5 * peakSpeedPerMs * remaining * remaining / acceleration;
}

function timeAtDistanceRatio(totalDistance, travelDurationMs, accelerationMs, ratio) {
  const target = Math.max(0, Math.min(1, Number(ratio) || 0)) * totalDistance;
  let low = 0;
  let high = travelDurationMs;
  for (let iteration = 0; iteration < 18; iteration += 1) {
    const middle = (low + high) / 2;
    if (trapezoidDistanceAtTime(totalDistance, travelDurationMs, accelerationMs, middle) < target) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

function trapezoidSpeedAtTime(totalDistance, travelDurationMs, accelerationMs, timeMs) {
  const distance = Math.max(0, Number(totalDistance) || 0);
  const duration = Math.max(1, Number(travelDurationMs) || 1);
  const acceleration = Math.min(duration / 2, Math.max(0, Number(accelerationMs) || 0));
  const time = Math.max(0, Math.min(duration, Number(timeMs) || 0));
  if (distance <= 0 || time <= 0 || time >= duration) return 0;
  const peakSpeedPerMs = distance / Math.max(1, duration - acceleration);
  if (acceleration < 1) return peakSpeedPerMs * 1000;
  if (time < acceleration) return peakSpeedPerMs * (time / acceleration) * 1000;
  if (time > duration - acceleration) return peakSpeedPerMs * ((duration - time) / acceleration) * 1000;
  return peakSpeedPerMs * 1000;
}

function sampleScenarioActor(actor, elapsedMs) {
  const metrics = routeMetrics(actor.path);
  const motion = actor.motion || {};
  const startDelayMs = Math.max(0, Number(motion.startDelayMs) || 0);
  const travelDurationMs = Math.max(1, Number(motion.travelDurationMs)
    || (metrics.total / Math.max(1, Number(motion.speedUnitsPerSecond) || 180) * 1000));
  const accelerationMs = Math.max(0, Number(motion.accelerationMs) || 0);
  const localTime = Number(elapsedMs) - startDelayMs;
  const utility = motion.utility;
  const utilityStartTravelMs = utility
    ? timeAtDistanceRatio(metrics.total, travelDurationMs, accelerationMs, utility.atDistanceRatio)
    : Number.POSITIVE_INFINITY;
  const utilityHoldMs = Math.max(0, Number(utility?.holdMs) || 0);
  let travelTime = Math.max(0, localTime);
  let action = localTime < 0 ? 'idle' : 'walk';
  let actionProgress = 0;
  if (utility && localTime >= utilityStartTravelMs) {
    if (localTime < utilityStartTravelMs + utilityHoldMs) {
      travelTime = utilityStartTravelMs;
      action = 'throw';
      actionProgress = (localTime - utilityStartTravelMs) / Math.max(1, utilityHoldMs);
    } else {
      travelTime = localTime - utilityHoldMs;
    }
  }
  travelTime = Math.max(0, Math.min(travelDurationMs, travelTime));
  const travelled = trapezoidDistanceAtTime(metrics.total, travelDurationMs, accelerationMs, travelTime);
  const movement = interpolateRouteDistance(actor.path, travelled);
  const arrivedForMs = Math.max(0, travelTime - travelDurationMs)
    + Math.max(0, localTime - travelDurationMs - utilityHoldMs);
  if (travelTime >= travelDurationMs) {
    const arrivalActionMs = Math.max(0, Number(motion.arrivalActionMs) || 0);
    if (motion.arrivalAction && arrivedForMs < arrivalActionMs) {
      action = motion.arrivalAction;
      actionProgress = arrivedForMs / Math.max(1, arrivalActionMs);
    } else {
      action = 'hold';
      actionProgress = 1;
    }
  }
  const speedUnitsPerSecond = action === 'walk' || action === 'run'
    ? Math.min(
      Number(motion.speedUnitsPerSecond) || 180,
      trapezoidSpeedAtTime(metrics.total, travelDurationMs, accelerationMs, travelTime),
    )
    : 0;
  if (action === 'walk' || action === 'run') action = speedUnitsPerSecond >= 182 ? 'run' : 'walk';
  return {
    ...movement,
    action,
    actionProgress: Math.max(0, Math.min(1, actionProgress)),
    speedUnitsPerSecond,
    locomotionCycle: travelled / DEMO_GAIT_CYCLE_UNITS,
    gaitCycleUnits: DEMO_GAIT_CYCLE_UNITS,
    utilityStartMs: startDelayMs + utilityStartTravelMs,
    utilityReleaseMs: startDelayMs + utilityStartTravelMs + utilityHoldMs * 0.58,
    utilityType: String(utility?.type || ''),
    startDelayMs,
    travelDurationMs,
  };
}

function boundedScenarioTime(value, durationMs) {
  return Math.max(0, Math.min(Math.max(1000, Number(durationMs) || 30000), Math.round(Number(value) || 0)));
}

export function createScenarioTimelineEvents(scenario) {
  if (!scenario || typeof scenario !== 'object') return [];
  const durationMs = Math.max(1000, Number(scenario.durationMs) || 30000);
  const actors = Array.isArray(scenario.actors) ? scenario.actors : [];
  const actorById = new Map(actors.map((actor) => [String(actor.id), actor]));
  const events = [];
  (scenario.tacticalPlan?.phases || []).forEach((phase, index) => {
    const id = typeof phase === 'object' ? String(phase.id || `phase-${index}`) : `phase-${index}`;
    const label = typeof phase === 'object' ? String(phase.label || id) : String(phase);
    events.push({
      id: `phase:${id}`,
      type: 'phase',
      label,
      timeMs: boundedScenarioTime(typeof phase === 'object' ? phase.startMs : index * durationMs / 5, durationMs),
      actorId: null,
      team: 0,
    });
  });
  actors.forEach((actor) => {
    if (!actor.motion?.utility) return;
    const sampled = sampleScenarioActor(actor, 0);
    events.push({
      id: `utility:${actor.id}`,
      type: 'utility',
      label: `${actor.name || actor.id} 投掷 ${String(actor.motion.utility.type || '道具')}`,
      timeMs: boundedScenarioTime(Math.ceil(sampled.utilityReleaseMs), durationMs),
      actorId: String(actor.id),
      team: Number(actor.team) || 0,
    });
  });
  (scenario.combatPlan?.events || []).forEach((combatEvent, index) => {
    const actor = actorById.get(String(combatEvent.attackerId));
    events.push({
      id: `combat:${combatEvent.id || index}`,
      type: ['contact', 'damage', 'kill'].includes(combatEvent.type) ? combatEvent.type : 'damage',
      label: String(combatEvent.label || combatEvent.id || '交火事件'),
      timeMs: boundedScenarioTime(combatEvent.timeMs, durationMs),
      actorId: String(combatEvent.attackerId || ''),
      victimId: String(combatEvent.victimId || ''),
      team: Number(actor?.team) || 0,
      damage: Math.max(0, Number(combatEvent.damage) || 0),
      weapon: String(combatEvent.weapon || actor?.weapon || ''),
    });
  });
  const carrier = actors.find((actor) => actor.hasBomb);
  const objective = scenario.roundFlow?.objective;
  if (carrier && objective?.mode === 'bomb') {
    events.push({
      id: `objective:plant:${carrier.id}`,
      type: 'objective',
      label: `${carrier.name || carrier.id} 完成下包`,
      timeMs: boundedScenarioTime(objective.plantAtMs, durationMs),
      actorId: String(carrier.id),
      team: Number(carrier.team) || 0,
    });
    if (objective.resolution === 'defused') {
      events.push({
        id: 'objective:defuse-start',
        type: 'objective',
        label: 'CT 开始拆除 C4',
        timeMs: boundedScenarioTime(objective.defuseStartMs, durationMs),
        actorId: null,
        team: 3,
      });
    }
  } else if (carrier && !objective) {
    const objectiveTime = Number(carrier.motion?.startDelayMs || 0)
      + Number(carrier.motion?.travelDurationMs || 0)
      + Number(carrier.motion?.utility?.holdMs || 0)
      + Number(carrier.motion?.arrivalActionMs || 0);
    events.push({
      id: `objective:${carrier.id}`,
      type: 'objective',
      label: `${carrier.name || carrier.id} 完成目标动作`,
      timeMs: boundedScenarioTime(objectiveTime, durationMs),
      actorId: String(carrier.id),
      team: Number(carrier.team) || 0,
    });
  }
  if (objective) {
    const resolutionLabel = objective.resolution === 'exploded'
      ? 'C4 爆炸'
      : objective.resolution === 'defused'
        ? 'C4 已拆除'
        : '回合时间结束';
    events.push({
      id: `objective:${objective.resolution}`,
      type: 'objective',
      label: resolutionLabel,
      timeMs: boundedScenarioTime(objective.resolveAtMs, durationMs),
      actorId: null,
      team: Number(objective.winnerTeam) || 0,
    });
    events.push({
      id: 'round:result',
      type: 'result',
      label: String(objective.resultLabel || resolutionLabel),
      timeMs: boundedScenarioTime(Number(objective.resolveAtMs) + 1, durationMs),
      actorId: null,
      team: Number(objective.winnerTeam) || 0,
    });
  }
  return events.sort((left, right) => left.timeMs - right.timeMs || left.id.localeCompare(right.id));
}

function sampleScenarioCombatActor(scenario, actor, elapsedMs) {
  const combatEvents = [...(scenario.combatPlan?.events || [])]
    .filter((event) => ['damage', 'kill'].includes(event.type))
    .sort((left, right) => Number(left.timeMs) - Number(right.timeMs) || String(left.id).localeCompare(String(right.id)));
  let health = 100;
  let deathAtMs = Number.POSITIVE_INFINITY;
  let deathEventId = '';
  combatEvents.forEach((event) => {
    if (String(event.victimId) !== String(actor.id) || Number(event.timeMs) > Number(elapsedMs)) return;
    health = Math.max(0, health - Math.max(0, Number(event.damage) || 0));
    if (event.type === 'kill') health = 0;
    if (health === 0 && !Number.isFinite(deathAtMs)) {
      deathAtMs = Number(event.timeMs);
      deathEventId = String(event.id || 'death');
    }
  });
  const movementTimeMs = Number.isFinite(deathAtMs) ? Math.min(Number(elapsedMs), deathAtMs) : Number(elapsedMs);
  const movement = sampleScenarioActor(actor, movementTimeMs);
  const attackEvent = combatEvents.find((event) => String(event.attackerId) === String(actor.id)
    && Number(elapsedMs) >= Number(event.timeMs) - 160
    && Number(elapsedMs) < Number(event.timeMs) + 560);
  const hitEvent = combatEvents.find((event) => String(event.victimId) === String(actor.id)
    && Number(elapsedMs) >= Number(event.timeMs)
    && Number(elapsedMs) < Number(event.timeMs) + 440);
  let action = movement.action;
  let actionProgress = movement.actionProgress;
  let combatEventId = '';
  let opponentId = '';
  if (Number.isFinite(deathAtMs)) {
    action = 'death';
    actionProgress = Math.max(0, Math.min(1, (Number(elapsedMs) - deathAtMs) / 900));
    combatEventId = deathEventId;
  } else if (hitEvent) {
    action = 'hit';
    actionProgress = Math.max(0, Math.min(1, (Number(elapsedMs) - Number(hitEvent.timeMs)) / 440));
    combatEventId = String(hitEvent.id || 'hit');
    opponentId = String(hitEvent.attackerId || '');
  } else if (attackEvent) {
    action = 'fire';
    actionProgress = Math.max(0, Math.min(1, (Number(elapsedMs) - Number(attackEvent.timeMs) + 160) / 720));
    combatEventId = String(attackEvent.id || 'fire');
    opponentId = String(attackEvent.victimId || '');
  }
  return {
    movement,
    action,
    actionProgress,
    health,
    dead: Number.isFinite(deathAtMs),
    deathAtMs,
    deathProgress: Number.isFinite(deathAtMs)
      ? Math.max(0, Math.min(1, (Number(elapsedMs) - deathAtMs) / 900))
      : 0,
    combatEventId,
    opponentId,
  };
}

export function createScenarioRoundState(scenario, actors, elapsedMs) {
  const objective = scenario?.roundFlow?.objective || {};
  const timeMs = Math.max(0, Number(elapsedMs) || 0);
  const alive = (actors || []).reduce((counts, actor) => {
    if (Boolean(actor.m_is_dead ?? actor.dead)) return counts;
    const team = Number(actor.m_team ?? actor.team);
    if (team === 2) counts.t += 1;
    if (team === 3) counts.ct += 1;
    return counts;
  }, { t: 0, ct: 0 });
  const mode = String(objective.mode || 'control');
  const resolution = String(objective.resolution || 'timeout');
  const plantAtMs = Number(objective.plantAtMs);
  const defuseStartMs = Number(objective.defuseStartMs);
  const resolveAtMs = Number(objective.resolveAtMs);
  const roundFinished = Number.isFinite(resolveAtMs) && timeMs >= resolveAtMs;
  let objectiveState = 'inactive';
  if (mode === 'bomb') {
    if (!Number.isFinite(plantAtMs) || timeMs < plantAtMs) objectiveState = 'carried';
    else if (roundFinished) objectiveState = resolution === 'defused' ? 'defused' : 'exploded';
    else if (resolution === 'defused' && Number.isFinite(defuseStartMs) && timeMs >= defuseStartMs) objectiveState = 'defusing';
    else objectiveState = 'planted';
  }
  return {
    alive,
    objectiveState,
    roundFinished,
    winnerTeam: roundFinished ? Number(objective.winnerTeam) || 0 : 0,
    resultLabel: roundFinished ? String(objective.resultLabel || '') : '',
    resolution,
    resolveAtMs: Number.isFinite(resolveAtMs) ? resolveAtMs : null,
    bombClockMs: mode === 'bomb' && Number.isFinite(plantAtMs) && timeMs >= plantAtMs && objectiveState !== 'defused'
      ? Math.max(0, plantAtMs + 40000 - timeMs)
      : null,
    defuseClockMs: objectiveState === 'defusing' && Number.isFinite(resolveAtMs)
      ? Math.max(0, resolveAtMs - timeMs)
      : null,
  };
}

export function createScenarioDemoFrame(mapId, scenarioPackage, liveValidation, elapsedMs = 0, scenarioIndex = 0) {
  const scenarios = Array.isArray(scenarioPackage?.scenarios) ? scenarioPackage.scenarios : [];
  const scenario = scenarios[Math.max(0, Math.min(scenarios.length - 1, Number(scenarioIndex) || 0))];
  if (!scenario || normalizeCs2MapId(scenarioPackage.mapId) !== normalizeCs2MapId(mapId)) {
    return createResourceDemoFrame(mapId, liveValidation, elapsedMs);
  }
  const durationMs = Math.max(1000, Number(scenario.durationMs) || 30000);
  const cycleElapsedMs = Math.min(durationMs, Math.max(0, Number(elapsedMs) || 0));
  const progress = cycleElapsedMs / durationMs;
  const sampledActors = (scenario.actors || []).map((actor) => ({
    actor,
    combat: sampleScenarioCombatActor(scenario, actor, cycleElapsedMs),
  }));
  const sampledById = new Map(sampledActors.map((item) => [String(item.actor.id), item]));
  const actors = sampledActors.map(({ actor, combat }, index) => {
    const { movement } = combat;
    const local = actor.id === 'ct-local' || (index === 0 && !scenario.actors.some((item) => item.id === 'ct-local'));
    const opponent = sampledById.get(combat.opponentId)?.combat?.movement?.position;
    const combatAngle = opponent
      ? Math.atan2(Number(opponent.y) - Number(movement.position.y), Number(opponent.x) - Number(movement.position.x)) * 180 / Math.PI + 90
      : movement.angle;
    const speedUnitsPerSecond = ['walk', 'run'].includes(combat.action) ? movement.speedUnitsPerSecond : 0;
    return {
      m_idx: actor.id || `demo-${index}`,
      m_name: actor.name || `战术单位 ${index + 1}`,
      m_team: Number(actor.team),
      m_health: combat.health,
      m_armor: 100,
      m_is_dead: combat.dead,
      m_is_local: local,
      m_eye_angle: combatAngle,
      m_position: movement.position,
      m_velocity: {
        x: Math.sin(combatAngle * Math.PI / 180) * speedUnitsPerSecond,
        y: Math.cos(combatAngle * Math.PI / 180) * speedUnitsPerSecond,
        z: 0,
      },
      m_animation: {
        action: combat.action,
        actionProgress: combat.actionProgress,
        locomotionCycle: movement.locomotionCycle,
        gaitCycleUnits: movement.gaitCycleUnits,
        speedUnitsPerSecond,
        routeProgress: movement.progress,
        role: actor.role,
        utilityType: movement.utilityType,
        deathProgress: combat.deathProgress,
        combatEventId: combat.combatEventId,
      },
      m_has_bomb: Boolean(actor.hasBomb),
      m_weapons: { m_active: { m_name: actor.weapon || (Number(actor.team) === 3 ? 'm4a1' : 'ak47') } },
    };
  });
  const site = positionOf(scenario.site);
  const carrier = actors.find((actor) => actor.m_has_bomb) || actors.find((actor) => actor.m_team === 2) || actors[0];
  const roundState = createScenarioRoundState(scenario, actors, cycleElapsedMs);
  const bombPlanted = ['planted', 'defusing'].includes(roundState.objectiveState);
  const bombPosition = roundState.objectiveState === 'carried' ? carrier?.m_position || site : site;
  const carrierDefinition = (scenario.actors || []).find((actor) => String(actor.id) === String(carrier?.m_idx));
  const plantCompleteMs = carrierDefinition
    ? Number(carrierDefinition.motion?.startDelayMs || 0)
      + Number(carrierDefinition.motion?.travelDurationMs || 0)
      + Number(carrierDefinition.motion?.utility?.holdMs || 0)
      + Number(carrierDefinition.motion?.arrivalActionMs || 0)
    : Number.POSITIVE_INFINITY;
  if (roundState.objectiveState === 'defusing') {
    const defuser = actors
      .filter((actor) => Number(actor.m_team) === 3 && !actor.m_is_dead)
      .sort((left, right) => {
        const leftDistance = Math.hypot(left.m_position.x - site.x, left.m_position.y - site.y, left.m_position.z - site.z);
        const rightDistance = Math.hypot(right.m_position.x - site.x, right.m_position.y - site.y, right.m_position.z - site.z);
        return leftDistance - rightDistance;
      })[0];
    if (defuser?.m_animation) {
      defuser.m_animation.action = 'defuse';
      defuser.m_animation.actionProgress = 1 - Number(roundState.defuseClockMs || 0) / 5000;
      defuser.m_animation.speedUnitsPerSecond = 0;
      defuser.m_velocity = { x: 0, y: 0, z: 0 };
    }
  }
  if (roundState.roundFinished) {
    actors.forEach((actor) => {
      if (!actor.m_is_dead && Number(actor.m_team) === roundState.winnerTeam && actor.m_animation) {
        actor.m_animation.action = 'victory';
        actor.m_animation.actionProgress = Math.min(1, Math.max(0, (cycleElapsedMs - Number(roundState.resolveAtMs)) / 900));
        actor.m_animation.speedUnitsPerSecond = 0;
        actor.m_velocity = { x: 0, y: 0, z: 0 };
      }
    });
  }
  const matchingUtility = (scenarioPackage.utility || []).filter((item) => {
    const name = String(item.name || '').toUpperCase();
    if (scenario.id.startsWith('a-')) return name.includes('A');
    if (scenario.id.startsWith('b-')) return name.includes('B');
    return /中|MID/.test(name);
  });
  const selectedUtility = matchingUtility.length ? matchingUtility : scenarioPackage.utility || [];
  const utilityActors = (scenario.actors || []).map((actor) => ({
    actor,
    movement: sampleScenarioActor(actor, cycleElapsedMs),
  })).filter(({ actor }) => actor.motion?.utility);
  const projectiles = utilityActors.flatMap(({ actor, movement }, index) => {
    const ageMs = cycleElapsedMs - movement.utilityReleaseMs;
    const item = selectedUtility[index % Math.max(1, selectedUtility.length)] || {};
    const type = normalizeProjectileType(actor.motion.utility.type || item.type || 'grenade');
    const flightDurationMs = 1700;
    const effectDurationMs = type === 'smoke'
      ? 12000
      : type === 'inferno'
        ? 7000
        : type === 'flashbang'
          ? 850
          : 1050;
    if (ageMs < 0 || ageMs > flightDurationMs + effectDurationMs) return [];
    const phase = Math.max(0, Math.min(1, ageMs / flightDurationMs));
    const start = sampleScenarioActor(actor, movement.utilityStartMs).position;
    const landing = sampleScenarioActor(actor, movement.utilityReleaseMs + flightDurationMs + 450).position;
    const deltaX = landing.x - start.x;
    const deltaY = landing.y - start.y;
    const deltaZ = landing.z - start.z;
    const position = {
      x: start.x + deltaX * phase,
      y: start.y + deltaY * phase,
      z: start.z + deltaZ * phase + Math.sin(Math.PI * phase) * 128,
    };
    const state = ageMs < flightDurationMs ? 'flight' : 'detonated';
    return [{
      m_entity_id: `demo-utility-${index}`,
      m_type: type,
      m_team: Number(actor.team),
      m_state: state,
      m_exploded: state === 'detonated',
      m_effect_age_ms: Math.max(0, ageMs - flightDurationMs),
      m_effect_duration_ms: effectDurationMs,
      m_effect_radius: type === 'smoke' ? 180 : type === 'inferno' ? 220 : type === 'flashbang' ? 260 : 190,
      m_velocity: state === 'flight' ? {
        x: deltaX / (flightDurationMs / 1000),
        y: deltaY / (flightDurationMs / 1000),
        z: deltaZ / (flightDurationMs / 1000),
      } : { x: 0, y: 0, z: 0 },
      m_position: position,
    }];
  });
  const deadDrops = actors.filter((actor) => actor.m_is_dead).map((actor) => ({
    m_entity_id: `demo-drop-${actor.m_idx}`,
    m_name: actor.m_weapons?.m_active?.m_name || 'weapon',
    m_position: actor.m_position,
  }));
  const rawBomb = roundState.objectiveState === 'exploded' || roundState.objectiveState === 'inactive'
    ? null
    : {
      ...bombPosition,
      m_is_planted: bombPlanted,
      m_is_defused: roundState.objectiveState === 'defused',
      m_state: roundState.objectiveState,
    };
  const frame = adaptCs2Frame({
    m_map: normalizeCs2MapId(mapId),
    m_local_team: 3,
    m_round_phase: roundState.roundFinished ? 'over' : 'preview',
    m_players: actors,
    m_bomb: rawBomb,
    m_projectiles: projectiles,
    m_dropped_weapons: deadDrops,
  });
  const timelineEvents = createScenarioTimelineEvents(scenario);
  const activeEvent = timelineEvents.filter((event) => event.timeMs <= cycleElapsedMs).at(-1) || timelineEvents[0] || null;
  const recentCombatEvents = timelineEvents.filter((event) => ['contact', 'damage', 'kill'].includes(event.type)
    && event.timeMs <= cycleElapsedMs
    && cycleElapsedMs - event.timeMs <= 2800);
  frame.demo = {
    scenarioId: scenario.id,
    scenarioName: scenario.name,
    scenarioIndex: scenarios.indexOf(scenario),
    scenarioCount: scenarios.length,
    durationMs,
    elapsedMs: cycleElapsedMs,
    progress,
    roundClockMs: Math.max(0, 115000 - Math.max(0, cycleElapsedMs - 2600)),
    bombPlanted,
    bombClockMs: roundState.bombClockMs,
    defuseClockMs: roundState.defuseClockMs,
    alive: roundState.alive,
    objectiveState: roundState.objectiveState,
    roundFinished: roundState.roundFinished,
    winnerTeam: roundState.winnerTeam,
    resultLabel: roundState.resultLabel,
    resolution: roundState.resolution,
    navigationMode: scenarioPackage.navigation?.mode || 'fallback',
    tacticalSide: scenario.tacticalPlan?.side || 'contested',
    tacticalPhase: Array.isArray(scenario.tacticalPlan?.phases) && scenario.tacticalPlan.phases.length
      ? [...scenario.tacticalPlan.phases]
        .filter((phase) => Number(phase?.startMs || 0) <= cycleElapsedMs)
        .at(-1)?.label || String(scenario.tacticalPlan.phases[0]?.label || scenario.tacticalPlan.phases[0])
      : '',
    simulationMode: scenarioPackage.simulation?.mode || 'normalized-progress-v0',
    timelineEvents,
    activeEvent,
    recentCombatEvents,
  };
  return frame;
}
